import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const CartContext = createContext();

/** The wishlist only syncs to the server for a signed-in user. */
const isSignedIn = () =>
  Boolean(localStorage.getItem('token') || sessionStorage.getItem('token'));

/**
 * Read a persisted list, tolerating absent or corrupt JSON.
 *
 * Used as a lazy `useState` initialiser rather than hydrating inside an
 * effect. Hydrating in an effect raced with the save effect below: both run
 * on the same commit, so the save wrote the initial empty array back over
 * localStorage before the loaded value had been applied. Under StrictMode's
 * double-invoke the second load pass then read that empty array back — so a
 * shopper's cart was emptied by any full page load or refresh.
 */
const readStored = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => readStored('bytegurukul_cart'));
  const [orders, setOrders] = useState(() => readStored('bytegurukul_orders'));
  const [wishlist, setWishlist] = useState(() => readStored('bytegurukul_wishlist'));

  useEffect(() => {
    // Signed in: the server copy is authoritative across devices. Merge it
    // over whatever this browser had cached.
    if (isSignedIn()) {
      api.get('/project-wishlist')
        .then(({ data }) => {
          const rows = data?.data || [];
          const projects = rows.map(r => r.project || r.Project).filter(Boolean);
          if (projects.length > 0) {
            setWishlist(prev => {
              const seen = new Set(projects.map(p => p.id));
              return [...projects, ...prev.filter(p => !seen.has(p.id))];
            });
          }
        })
        .catch(() => { /* offline or signed out mid-load: keep the local copy */ });
    }
  }, []);

  // Save to localStorage whenever cart or orders change
  useEffect(() => {
    localStorage.setItem('bytegurukul_cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('bytegurukul_orders', JSON.stringify(orders));
  }, [orders]);

  useEffect(() => {
    localStorage.setItem('bytegurukul_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  // Add a project to the cart.
  //
  // These are digital goods — source code you own once. Adding an item that's
  // already in the cart is a no-op, not an increment. The old version bumped
  // the quantity, and getCartTotal charged price × quantity, but the checkout
  // only ever creates one order per item at price × 1 — so the cart showed a
  // total higher than what was actually charged. Quantity is pinned to 1.
  const addToCart = (project) => {
    setCart(prevCart => {
      if (prevCart.some(item => item.id === project.id)) return prevCart;
      return [...prevCart, { ...project, quantity: 1 }];
    });
  };

  // Remove project from cart
  const removeFromCart = (projectId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== projectId));
  };

  // Clear entire cart
  const clearCart = () => {
    setCart([]);
  };

  // Each item is bought once, so the total and count are over unique items.
  // This now matches exactly what the checkout charges.
  const getCartTotal = () => {
    return cart.reduce((total, item) => total + Number(item.price || 0), 0);
  };

  const getCartItemsCount = () => cart.length;

  // Create order (checkout)
  const createOrder = (orderData) => {
    const newOrder = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      items: [...cart],
      total: getCartTotal(),
      status: 'completed',
      paymentMethod: orderData.paymentMethod,
      ...orderData
    };
    
    setOrders(prevOrders => [newOrder, ...prevOrders]);
    clearCart(); // Clear cart after successful order
    
    return newOrder;
  };

  // Check if project is in cart
  const isInCart = (projectId) => {
    return cart.some(item => item.id === projectId);
  };

  // Add project to wishlist.
  //
  // The list is kept in localStorage so it still works signed out, and is
  // mirrored to /api/project-wishlist when there is a token — previously it
  // lived only in localStorage, so a saved project vanished on a different
  // device even though the server route and table already existed.
  const addToWishlist = (project) => {
    setWishlist(prevWishlist => {
      const existingItem = prevWishlist.find(item => item.id === project.id);
      if (!existingItem) {
        return [...prevWishlist, project];
      }
      return prevWishlist;
    });
    if (isSignedIn()) {
      api.post(`/project-wishlist/${project.id}`)
        .catch(err => console.error('Wishlist sync failed:', err.message));
    }
  };

  // Remove project from wishlist
  const removeFromWishlist = (projectId) => {
    setWishlist(prevWishlist => prevWishlist.filter(item => item.id !== projectId));
    if (isSignedIn()) {
      api.delete(`/project-wishlist/${projectId}`)
        .catch(err => console.error('Wishlist sync failed:', err.message));
    }
  };

  // Check if project is in wishlist
  const isInWishlist = (projectId) => {
    return wishlist.some(item => item.id === projectId);
  };

  const value = {
    cart,
    orders,
    addToCart,
    removeFromCart,
    clearCart,
    getCartTotal,
    getCartItemsCount,
    createOrder,
    isInCart,
    wishlist,
    addToWishlist,
    removeFromWishlist,
    isInWishlist
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

// Custom hook to use cart context
export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}